import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerCampaign } from '@/api/routes/partners/partners.campaigns.api';
import { formatCampaignPeriod, formatSlots } from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  campaign: PartnerCampaign;
}

/** Who sees the campaign, where and when: decided by the Bodyprint, read-only here. */
const CampaignSummary = ({ campaign }: Props) => {
  const t = useTranslate('domain.results.components.CampaignSummary');

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Item
        label={t('audience')}
        value={
          campaign.requiredConsumerProduct
            ? (campaign.audience?.description ?? t('plusMembers'))
            : (campaign.audience?.description ?? t('audienceFallback'))
        }
        hint={campaign.requiredConsumerProduct ? t('plusHint') : t('audienceHint')}
      />
      <Item label={t('slots')} value={formatSlots(campaign.slots)} />
      <Item label={t('period')} value={formatCampaignPeriod(campaign)} />
    </Paper>
  );
};

export default CampaignSummary;

const Item = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Stack gap={0.25} minWidth={0}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
    {hint ? (
      <Box component="span" sx={{ typography: 'caption', color: 'text.secondary' }}>
        {hint}
      </Box>
    ) : null}
  </Stack>
);

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
  },
};
